import React, { useState } from "react";
import axios from "axios";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Cake, Users, PartyPopper, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const saveLocal = (key, entry) => {
  try {
    const list = JSON.parse(localStorage.getItem(key) || "[]");
    list.push({ ...entry, ts: Date.now() });
    localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {
    /* ignore */
  }
};

const Community = () => {
  const { BIRTHDAY_MONTHS, CABIN_OPTIONS, EVENT } = useCruise();
  const monthsLabel = BIRTHDAY_MONTHS.length === 3
    ? `${BIRTHDAY_MONTHS[0]}, ${BIRTHDAY_MONTHS[1]} or ${BIRTHDAY_MONTHS[2]}`
    : BIRTHDAY_MONTHS.join(", ");
  const [bday, setBday] = useState({ name: "", email: "", phone: "", month: "", day: "", note: "" });
  const [bdayDone, setBdayDone] = useState(false);

  const [rm, setRm] = useState({ name: "", email: "", phone: "", gender: "", cabin: "", about: "" });
  const [rmDone, setRmDone] = useState(false);

  const submitBday = async (e) => {
    e.preventDefault();
    if (!bday.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bday.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (bday.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    if (!bday.month) {
      toast.error("Please select your birthday month.");
      return;
    }
    try {
      await axios.post(`${API}/signups/birthday`, { ...bday, cruise: EVENT.id });
      saveLocal("hhas_birthdays", bday);
      setBdayDone(true);
      toast.success(`🎂 You're on the list, ${bday.name.split(" ")[0]}! We'll celebrate you onboard.`);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const submitRoommate = async (e) => {
    e.preventDefault();
    if (!rm.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rm.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (rm.phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    if (!rm.cabin) {
      toast.error("Please select your preferred cabin.");
      return;
    }
    try {
      await axios.post(`${API}/signups/roommate`, { ...rm, cruise: EVENT.id });
      saveLocal("hhas_roommates", rm);
      setRmDone(true);
      toast.success("Request received! We'll help match you with a fellow House Head.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="community" className="relative bg-[#0A0A0A] py-24">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center mb-14">
          <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-3">
            Come For The Cruise. Leave As Family.
          </p>
          <h2 className="font-display uppercase text-white text-4xl sm:text-6xl">
            Join The <span className="text-[#E50914]">Family</span>
          </h2>
          <div className="flex justify-center mt-4">
            <span className="section-line" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Birthday Shout-Outs */}
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-7 sm:p-9">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[var(--hh-gold)]/15 text-[var(--hh-gold)]">
                <Cake size={22} />
              </span>
              <h3 className="font-cond font-bold uppercase text-white text-2xl tracking-wide">Birthday Shout-Outs</h3>
            </div>
            <p className="text-white/55 text-sm mb-6">
              Celebrating a <span className="text-white">{monthsLabel}</span> birthday? Get a special
              onboard shout-out, gift exchange and birthday love from the whole House Heads family.
            </p>

            {bdayDone ? (
              <div className="flex flex-col items-center text-center py-8">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1B8A5A] text-white mb-4">
                  <PartyPopper size={26} />
                </span>
                <p className="font-cond uppercase tracking-wide text-white text-lg">You're on the list!</p>
                <p className="text-white/55 text-sm mt-1">We can't wait to celebrate you at sea.</p>
                <button
                  onClick={() => { setBdayDone(false); setBday({ name: "", email: "", phone: "", month: "", day: "", note: "" }); }}
                  className="mt-5 text-[#E50914] font-cond uppercase text-xs tracking-widest hover:underline"
                >
                  Add another
                </button>
              </div>
            ) : (
              <form onSubmit={submitBday} className="space-y-4">
                <div>
                  <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Full Name *</Label>
                  <Input
                    value={bday.name}
                    onChange={(e) => setBday({ ...bday, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="birthday-name-input"
                    required
                    className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Email *</Label>
                    <Input
                      type="email"
                      value={bday.email}
                      onChange={(e) => setBday({ ...bday, email: e.target.value })}
                      placeholder="you@email.com"
                      data-testid="birthday-email-input"
                      required
                      className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Phone *</Label>
                    <Input
                      type="tel"
                      value={bday.phone}
                      onChange={(e) => setBday({ ...bday, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                      data-testid="birthday-phone-input"
                      required
                      className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Birthday Month *</Label>
                    <Select value={bday.month} onValueChange={(v) => setBday({ ...bday, month: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus:ring-[#E50914]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {BIRTHDAY_MONTHS.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Day (optional)</Label>
                    <Input
                      value={bday.day}
                      onChange={(e) => setBday({ ...bday, day: e.target.value })}
                      placeholder="e.g. 15"
                      className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">A Fun Note (optional)</Label>
                  <Textarea
                    value={bday.note}
                    onChange={(e) => setBday({ ...bday, note: e.target.value })}
                    placeholder="Anything you'd like us to know?"
                    className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914] min-h-[80px]"
                  />
                </div>
                <Button type="submit" className="w-full bg-[var(--hh-gold)] hover:bg-[#e0ad38] text-[#0A0A0A] font-cond uppercase tracking-widest rounded-full py-6 h-auto">
                  <Cake size={18} className="mr-2" /> Add My Birthday
                </Button>
              </form>
            )}
          </div>

          {/* Roommate Match */}
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-7 sm:p-9">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914]/15 text-[#E50914]">
                <Users size={22} />
              </span>
              <h3 className="font-cond font-bold uppercase text-white text-2xl tracking-wide">Roommate Match</h3>
            </div>
            <p className="text-white/55 text-sm mb-6">
              Sailing solo? Request a roommate to <span className="text-white">share a cabin and split the fare</span>.
              We'll help connect you with a fellow House Head looking to do the same.
            </p>

            {rmDone ? (
              <div className="flex flex-col items-center text-center py-8">
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1B8A5A] text-white mb-4">
                  <Check size={26} />
                </span>
                <p className="font-cond uppercase tracking-wide text-white text-lg">Request received!</p>
                <p className="text-white/55 text-sm mt-1">We'll reach out about your roommate match.</p>
                <button
                  onClick={() => { setRmDone(false); setRm({ name: "", email: "", phone: "", gender: "", cabin: "", about: "" }); }}
                  className="mt-5 text-[#E50914] font-cond uppercase text-xs tracking-widest hover:underline"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submitRoommate} className="space-y-4">
                <div>
                  <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Full Name *</Label>
                  <Input
                    value={rm.name}
                    onChange={(e) => setRm({ ...rm, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="roommate-name-input"
                    required
                    className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Email *</Label>
                    <Input
                      type="email"
                      value={rm.email}
                      onChange={(e) => setRm({ ...rm, email: e.target.value })}
                      placeholder="you@email.com"
                      data-testid="roommate-email-input"
                      required
                      className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Phone *</Label>
                    <Input
                      type="tel"
                      value={rm.phone}
                      onChange={(e) => setRm({ ...rm, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                      data-testid="roommate-phone-input"
                      required
                      className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Roommate Pref.</Label>
                    <Select value={rm.gender} onValueChange={(v) => setRm({ ...rm, gender: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus:ring-[#E50914]">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Any">Any</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Male">Male</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">Preferred Cabin</Label>
                    <Select value={rm.cabin} onValueChange={(v) => setRm({ ...rm, cabin: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus:ring-[#E50914]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {CABIN_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 font-cond uppercase text-xs tracking-widest">A Bit About You (optional)</Label>
                  <Textarea
                    value={rm.about}
                    onChange={(e) => setRm({ ...rm, about: e.target.value })}
                    placeholder="Early bird or night owl? Neat or relaxed? Anything helpful for matching."
                    className="mt-1.5 bg-[#0A0A0A] border-white/15 text-white rounded-lg focus-visible:ring-[#E50914] min-h-[80px]"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest rounded-full py-6 h-auto">
                  <Users size={18} className="mr-2" /> Request A Roommate
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-8">
          Your details are securely saved and our team will follow up to confirm.
        </p>
      </div>
    </section>
  );
};

export default Community;
